import React, {
  createContext,
  ReactNode,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { isBrowser } from '@agria/utils';

// --- Session Storage Key ---
const SESSION_STORAGE_KEY = 'dev_experiment_overrides';

// --- Polling Interval Constant ---
const POLLING_INTERVAL_MS = 1000; // Check every 1 second

export interface ExperimentsContextProps {
  experiments: Record<string, any>;
  setExperiments: React.Dispatch<React.SetStateAction<object>>;
}

const defaultContextValue = {
  experiments: {},
  setExperiments: () => {},
};

export const ExperimentsContext =
  createContext<ExperimentsContextProps>(defaultContextValue);
ExperimentsContext.displayName = 'ExperimentsContext';

export const ExperimentsContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const actualExperimentsRef = useRef<Record<string, any>>({});
  const loggedExperimentsRef = useRef<Set<string>>(new Set());
  const previousWindowExperimentsStringRef = useRef<string | null>(null);

  // Determine isTestMode and enabledExperiments (runs once)
  const { isTestMode, enabledExperiments } = useMemo(() => {
    // ... (logic as before) ...
    let enabledExps: string[] = [];

    if (isBrowser) {
      const params = new URLSearchParams(window.location.search);
      const expParam = params.get('experiment');
      if (expParam) {
        const experiments = expParam.split(',').map((e) => e.trim());
        enabledExps.push(...experiments.filter((e) => e.startsWith('AGRIA')));
      }
    }

    if (process.env.GATSBY_ENABLED_EXPERIMENTS) {
      const envExperiments = process.env.GATSBY_ENABLED_EXPERIMENTS.split(',')
        .map((e) => e.trim())
        .filter((e) => e.startsWith('AGRIA'));
      enabledExps.push(...envExperiments);
    }

    enabledExps = [...new Set(enabledExps)];

    const isTest =
      process.env.GATSBY_ENVIRONMENT_TYPE === 'test' ||
      (isBrowser &&
        (window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1' ||
          window.location.hostname.includes('.local')));

    return {
      isTestMode: isTest,
      enabledExperiments: enabledExps,
    };
  }, []);

  // --- Apply Persisted Overrides on Load (Only in Test Mode) ---
  useEffect(() => {
    if (isBrowser && isTestMode) {
      // Only run if in test mode
      try {
        const storedOverridesString =
          sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (storedOverridesString) {
          if (isTestMode)
            console.log(
              '🧪 Applying overrides from sessionStorage:',
              storedOverridesString
            ); // Gated log
          const storedOverrides = JSON.parse(storedOverridesString);
          if (!window.experiments) {
            window.experiments = {};
          }
          Object.assign(window.experiments, storedOverrides);
        }
      } catch (error) {
        if (isTestMode)
          console.error('Error applying overrides from sessionStorage:', error); // Gated error
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
    }
  }, [isTestMode]);

  // Log test mode status (Only in Test Mode)
  useEffect(() => {
    if (isTestMode) {
      // Gated log
      if (enabledExperiments.length > 0) {
        console.log(
          `🧪 Experiment test mode active. Defaulting enabled experiments (${enabledExperiments.join(
            ', '
          )}) to "variation" unless overridden by window.experiments/sessionStorage.`
        );
      } else {
        console.log(
          '🧪 Experiment test mode active (no specific experiments enabled via ENV/URL).'
        );
      }
    }
  }, [isTestMode, enabledExperiments]);

  // --- Polling Effect for Reload & Persistence (Only in Test Mode) ---
  useEffect(() => {
    // Only poll in the browser and when in test mode
    if (!isBrowser || !isTestMode) {
      return; // Exit if not in test mode
    }

    // Gated logs from here down
    if (isTestMode)
      console.log(
        `🧪 Starting window.experiments polling for reload/persistence (interval: ${POLLING_INTERVAL_MS}ms)`
      );

    try {
      previousWindowExperimentsStringRef.current = JSON.stringify(
        window.experiments || {}
      );
    } catch (e) {
      if (isTestMode)
        console.error('Error stringifying initial window.experiments', e); // Gated error
      previousWindowExperimentsStringRef.current = '{}';
    }

    const intervalId = setInterval(() => {
      try {
        const currentWindowExperiments = window.experiments || {};
        const currentWindowExperimentsString = JSON.stringify(
          currentWindowExperiments
        );

        if (
          currentWindowExperimentsString !==
          previousWindowExperimentsStringRef.current
        ) {
          if (isTestMode)
            console.log('🔄 Detected change in window.experiments.'); // Gated log

          try {
            if (isTestMode)
              console.log(
                `💾 Persisting to sessionStorage (${SESSION_STORAGE_KEY}):`,
                currentWindowExperimentsString
              ); // Gated log
            sessionStorage.setItem(
              SESSION_STORAGE_KEY,
              currentWindowExperimentsString
            );
            previousWindowExperimentsStringRef.current =
              currentWindowExperimentsString;
          } catch (error) {
            if (isTestMode)
              console.error(
                'Error persisting overrides to sessionStorage:',
                error
              ); // Gated error
          }

          if (isTestMode) console.warn(`🔌 Reloading page...`); // Gated warn
          window.location.reload();
        }
      } catch (error) {
        if (isTestMode)
          console.error(
            'Error comparing/stringifying window.experiments during polling:',
            error
          ); // Gated error
      }
    }, POLLING_INTERVAL_MS);

    // Cleanup function
    return () => {
      if (isTestMode) console.log('🧪 Stopping window.experiments polling.'); // Gated log
      clearInterval(intervalId);
    };
  }, [isTestMode]); // Only depends on isTestMode

  // Create the experiments proxy object (runs once)
  const experiments = useMemo(
    () =>
      new Proxy(
        {},
        {
          get: (_, prop) => {
            if (typeof prop === 'string') {
              // Priority 1: window.experiments
              if (
                isBrowser &&
                window.experiments &&
                window.experiments.hasOwnProperty(prop)
              ) {
                const windowValue = window.experiments[prop];
                const logKey = `window-${prop}-${windowValue}`;
                // Log only in test mode AND if not previously logged
                if (isTestMode && !loggedExperimentsRef.current.has(logKey)) {
                  // Gated log
                  console.log(
                    `🧪 Using experiment from window.experiments: ${prop} = "${windowValue}" (may be from sessionStorage)`
                  );
                  loggedExperimentsRef.current.add(logKey);
                }
                return windowValue;
              }

              // Priority 2: Internal state
              const explicitValue = actualExperimentsRef.current[prop];
              if (explicitValue !== undefined) {
                const logKey = `internal-${prop}-${explicitValue}`;
                // Log only in test mode AND if not previously logged
                if (isTestMode && !loggedExperimentsRef.current.has(logKey)) {
                  // Gated log
                  console.log(
                    `🧪 Using experiment from internal state: ${prop} = "${explicitValue}"`
                  );
                  loggedExperimentsRef.current.add(logKey);
                }
                return explicitValue;
              }

              // Priority 3: Test mode fallback
              if (
                isTestMode && // Check isTestMode here too
                prop.startsWith('AGRIA') &&
                enabledExperiments.includes(prop)
              ) {
                const logKey = `test-mode-${prop}`;
                // Log only in test mode AND if not previously logged
                if (isTestMode && !loggedExperimentsRef.current.has(logKey)) {
                  // Gated log
                  console.log(
                    `🧪 Using test mode default: ${prop} = "variation"`
                  );
                  loggedExperimentsRef.current.add(logKey);
                }
                return 'variation';
              }
            }
            return undefined;
          },
        }
      ),
    // isTestMode added as dependency to proxy memoization, as it affects getter behavior
    [isTestMode, enabledExperiments]
  );

  // Function to update the internal experiments state (actualExperimentsRef)
  const setExperiments = (updater) => {
    if (typeof updater === 'function') {
      actualExperimentsRef.current = updater(actualExperimentsRef.current);
    } else {
      actualExperimentsRef.current = updater;
    }
    // Note: If you re-introduce forceUpdate, gate any related logs too.
  };

  // Memoize the context value
  const contextValue = useMemo(
    () => ({
      experiments,
      setExperiments,
    }),
    // experiments depends on isTestMode now, so contextValue implicitly does too
    [experiments]
  );

  return (
    <ExperimentsContext.Provider value={contextValue}>
      {children}
    </ExperimentsContext.Provider>
  );
};
